import React, { useState, useEffect, useMemo } from 'react';
import { Search, Loader2, AlertCircle, Briefcase, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';
import { PageHeader } from '@/components/blocks/page-header';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { getRoleService } from '@/services/roleService';
import type { RoleListItem } from '@/types/role';
import { RoleDetailDialog } from '@/components/RolesAndRates/RoleDetailDialog';
import { ProjectRoleOverridesTab } from '@/components/RolesAndRates/ProjectRoleOverridesTab';
import { EmployeeRoleOverridesTab } from '@/components/RolesAndRates/EmployeeRoleOverridesTab';
import { formatPercentage } from '@/utils/formatters';
import { useDataSource } from '@/contexts/DataSourceContext';
import { usePermissions } from '@/contexts/PermissionContext';
import { AccessRestricted } from '@/components/shared/AccessRestricted';

interface RolesAndRatesPageProps {
  onNavigate?: (path: string) => void;
}

export const RolesAndRatesPage: React.FC<RolesAndRatesPageProps> = ({ onNavigate }) => {
  const { getScreenMode } = useDataSource();
  const { canManageRates } = usePermissions();
  const screenMode = getScreenMode('roles-rates');
  const roleService = useMemo(() => getRoleService(screenMode), [screenMode]);
  
  const [roles, setRoles] = useState<RoleListItem[]>([]);
  const [filteredRoles, setFilteredRoles] = useState<RoleListItem[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedRoleId, setSelectedRoleId] = useState<number | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);

  // Debug logging
  useEffect(() => {
  }, [selectedRoleId]);

  const loadRoles = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const data = await roleService.getAllRoles();
      setRoles(data);
      filterRoles(data, searchQuery, showArchived);
    } catch (err) {
      console.error('Error loading roles:', err);
      setError('Failed to load roles. Please try again.');
      toast.error('Failed to load roles', {
        description: screenMode === 'live' ? 'Could not connect to the API. Please check your connection.' : undefined,
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadRoles();
  }, [screenMode]);

  useEffect(() => {
    filterRoles(roles, searchQuery, showArchived);
  }, [searchQuery, showArchived, roles]);

  const filterRoles = (rolesList: RoleListItem[], query: string, includeArchived: boolean) => {
    let filtered = rolesList;

    // Filter by status
    if (!includeArchived) {
      filtered = filtered.filter(r => r.status === 'Active');
    }

    // Filter by search query
    if (query.trim()) {
      const lowerQuery = query.toLowerCase();
      filtered = filtered.filter(r =>
        r.name.toLowerCase().includes(lowerQuery) ||
        r.description?.toLowerCase().includes(lowerQuery)
      );
    }

    setFilteredRoles(filtered);
    setCurrentPage(1); // Reset to first page when filters change
  };

  // Pagination calculations
  const totalPages = Math.ceil(filteredRoles.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedRoles = filteredRoles.slice(startIndex, endIndex);

  const handleRoleUpdated = () => {
    setSelectedRoleId(null);
    loadRoles();
  };

  // ── Permission gate ────────────────────────────────────────────────────
  if (!canManageRates()) {
    return (
      <div className="space-y-6">
        <PageHeader
          title="Roles & Rates"
          size={36}
          showActions={false}
          showBreadcrumb={false}
          icon={<Briefcase />}
        />
        <AccessRestricted message="You don't have permission to access Roles & Rates. Contact your administrator to request access." />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <PageHeader
        title="Roles & Rates"

        size={36}
        showActions={false}
        showBreadcrumb={false}
        icon={<Briefcase />}
      />

      {/* Tabs for Phase 1 and Phase 2 */}
      <Tabs defaultValue="roles" className="space-y-4">
        <TabsList>
          <TabsTrigger value="roles">Roles & Rates</TabsTrigger>
          <TabsTrigger value="project-overrides">Project Overrides</TabsTrigger>
          <TabsTrigger value="employee-overrides">Employee Overrides</TabsTrigger>
        </TabsList>

        <TabsContent value="roles" className="space-y-4">

          {/* Main Content Card */}
          <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <CardTitle>Roles & Rates</CardTitle>
              <CardDescription>
                View job roles and their billing and cost rates
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Filters */}
          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search roles..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <Button
              variant={showArchived ? "default" : "outline"}
              onClick={() => setShowArchived(!showArchived)}
            >
              {showArchived ? 'Active Only' : 'Show Archived'}
            </Button>
          </div>

          {/* Error State */}
          {error && (
            <Alert variant="destructive" className="mb-6">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {/* Loading State */}
          {isLoading && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          )}

          {/* Empty State */}
          {!isLoading && filteredRoles.length === 0 && (
            <div className="text-center py-12">
              <p className="text-muted-foreground">
                {searchQuery ? 'No roles match your search' : 'No roles found'}
              </p>
            </div>
          )}

          {/* Roles Table */}
          {!isLoading && filteredRoles.length > 0 && (
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left p-2 h-10 font-medium">Role</th>
                    <th className="text-left p-2 h-10 font-medium">Description</th>
                    <th className="text-right p-2 h-10 font-medium">Cost Rate<br/>(Hour)</th>
                    <th className="text-right p-2 h-10 font-medium">Cost Rate<br/>(Day)</th>
                    <th className="text-right p-2 h-10 font-medium">Bill Rate<br/>(Hour)</th>
                    <th className="text-right p-2 h-10 font-medium">Bill Rate<br/>(Day)</th>
                    <th className="text-right p-2 h-10 font-medium">Margin</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {paginatedRoles.map((role) => {
                    const costHourly = role.currentCostRate || 0;
                    const costDaily = costHourly * 8;
                    const chargeHourly = role.currentBillingRate || 0;
                    const chargeDaily = chargeHourly * 8;
                    const margin = costHourly > 0 ? ((chargeHourly - costHourly) / chargeHourly * 100) : 0;
                    
                    return (
                      <tr
                        key={role.id}
                        className="hover:bg-muted/30 cursor-pointer transition-colors"
                        onClick={() => {
                          setSelectedRoleId(role.id);
                        }}
                      >
                        <td className="p-2">
                          <div className="font-medium">{role.name}</div>
                        </td>
                        <td className="p-2">
                          <div className="text-muted-foreground truncate max-w-md">
                            {role.description || '—'}
                          </div>
                        </td>
                        <td className="p-2 text-right">
                          {role.currentCostRate ? (
                            <span className="font-medium">£{costHourly.toFixed(2)}</span>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </td>
                        <td className="p-2 text-right">
                          {role.currentCostRate ? (
                            <span className="font-medium">£{costDaily.toFixed(2)}</span>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </td>
                        <td className="p-2 text-right">
                          {role.currentBillingRate ? (
                            <span className="font-medium text-green-600">£{chargeHourly.toFixed(2)}</span>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </td>
                        <td className="p-2 text-right">
                          {role.currentBillingRate ? (
                            <span className="font-medium text-green-600">£{chargeDaily.toFixed(2)}</span>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </td>
                        <td className="p-2 text-right">
                          {role.currentBillingRate && role.currentCostRate ? (
                            <span className="font-medium text-blue-600">
                              {margin.toFixed(0)}%
                            </span>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>

              {/* Pagination */}
              {!isLoading && filteredRoles.length > 0 && (
                <div className="flex items-center justify-between px-2 py-3 border-t">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <span>Showing {startIndex + 1} to {Math.min(endIndex, filteredRoles.length)} of {filteredRoles.length} results</span>
                    <span className="hidden sm:inline">•</span>
                    <div className="flex items-center gap-2">
                      <span className="hidden sm:inline">Per page:</span>
                      <Select
                        value={itemsPerPage.toString()}
                        onValueChange={(value) => {
                          setItemsPerPage(Number(value));
                          setCurrentPage(1);
                        }}
                      >
                        <SelectTrigger className="h-8 w-auto min-w-[60px]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="10">10</SelectItem>
                          <SelectItem value="25">25</SelectItem>
                          <SelectItem value="50">50</SelectItem>
                          <SelectItem value="100">100</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(1)}
                      disabled={currentPage === 1}
                      className="h-8 w-8 p-0"
                    >
                      <ChevronsLeft className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(currentPage - 1)}
                      disabled={currentPage === 1}
                      className="h-8 w-8 p-0"
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <span className="text-sm font-medium">
                      Page {currentPage} of {totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(currentPage + 1)}
                      disabled={currentPage === totalPages}
                      className="h-8 w-8 p-0"
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setCurrentPage(totalPages)}
                      disabled={currentPage === totalPages}
                      className="h-8 w-8 p-0"
                    >
                      <ChevronsRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
        </TabsContent>

        <TabsContent value="project-overrides">
          <Card>
            <CardContent className="pt-6">
              <ProjectRoleOverridesTab />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="employee-overrides">
          <Card>
            <CardContent className="pt-6">
              <EmployeeRoleOverridesTab />
            </CardContent>
          </Card>
        </TabsContent>

      </Tabs>

      {/* Dialogs */}
      {selectedRoleId !== null && (
        <RoleDetailDialog
          roleId={selectedRoleId}
          open={true}
          onOpenChange={(open) => {
            if (!open) {
              setSelectedRoleId(null);
            }
          }}
          onSuccess={handleRoleUpdated}
        />
      )}
    </div>
  );
};
